// @flow
import { addMonths } from 'date-fns';
import { toDate } from 'date-functions';

import describeMonths, { getMonthName } from './describeMonths';
import listWithBullets from './listWithBullets';
import type { RequestGeneratorClient } from './types';

const getDocumentRequestsForAccount = ({ statementDayOfMonth, monthlyData, name }) => {
  let monthsWithDataRequested = monthlyData
    .filter(monthData => monthData.status === 'DATA_REQUESTED')
    .map(monthData => toDate(monthData.yearMonth));
  if (monthsWithDataRequested.length === 0) {
    return '';
  }
  const isMidMonthStatement =
    statementDayOfMonth !== 1 && statementDayOfMonth !== 31 && statementDayOfMonth !== null;
  let accountStatementPrefix = '';
  let monthPrefix = '';
  if (isMidMonthStatement) {
    // For mid-month statements, the month being requested represents the *start* of the statement
    // period that is needed, so the months are shifted by one so that request asks for the *end*
    // date of that statement.
    monthsWithDataRequested = monthsWithDataRequested.map(month => addMonths(month, 1));
    accountStatementPrefix = 'Ending ';
    if (statementDayOfMonth > 1 && statementDayOfMonth <= 10) {
      monthPrefix = 'early ';
    }
    if (statementDayOfMonth > 10 && statementDayOfMonth <= 20) {
      monthPrefix = 'mid ';
    }
    if (statementDayOfMonth > 20 && statementDayOfMonth <= 30) {
      monthPrefix = 'late ';
    }
  }
  const monthListing = describeMonths(monthsWithDataRequested, {
    formatMonth: date => `${monthPrefix}${getMonthName(date)}`,
  });
  return `${name} - ${accountStatementPrefix}${monthListing}`;
};

const getAccountDocumentRequests = accounts =>
  [...accounts]
    .sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      if (aName > bName) return 1;
      if (aName < bName) return -1;
      return 0;
    })
    .map(getDocumentRequestsForAccount)
    .filter(s => s);

const requestAccountDocuments = ({ accounts }: RequestGeneratorClient): string => {
  const accountDocumentRequests = getAccountDocumentRequests(accounts);
  if (accountDocumentRequests.length === 0) {
    return '';
  }
  return `\
We require the following documents to complete your monthly bookkeeping:
${listWithBullets(accountDocumentRequests)}\
`;
};

export default requestAccountDocuments;
